import { useCallback, useEffect, useState } from 'react'
import { Button, Flex, Form, Skeleton } from 'antd'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams } from 'react-router-dom'
import { yupResolver } from '@hookform/resolvers/yup'
import { SYSTEM_MESSAGE_IDS } from 'src/app/shared/constants/systemMessages.constants'
import { menuUrls } from 'src/app/shared/constants/menu.constants'
import BankInfo from '../components/BankInfo'
import SelectClient from '../components/SelectClient'
import ActionConfirmationModal from 'src/app/shared/components/modal/action-confirmation-modal'
import { DangerIcon } from 'src/assets/icons'
import { formatDataBeforeSubmit, generateValidationSchema, mappingDataToFormCopy } from '../components/utils'
import { IBankUserSetup, IGetListRole } from 'src/app/shared/models/bankUserSetup.model'
import { addNewBankUser, getBankUserDetail } from 'src/app/shared/services/BankUserService'
import RichTextField from 'src/app/shared/components/common/rich-text-field'
import { SERVER_STATUS_CODES } from 'src/app/shared/constants/serverResponseStatusCodes.constants'
import '../components/index.scss'
import { STATUS_CODES, priGrpTypCodeName } from 'src/app/shared/constants/common.constants'
import { getUserRoleListByType } from 'src/app/shared/services/ClientUserSetupService'
import SelectCpn from '../components/SelectCpn'

const BankSetupCopy = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const params = useParams()
  const { id } = params
  const [isLoading, setIsLoading] = useState(false)
  const [usernameConfirm, setUsernameConfirm] = useState('')
  const [isFetched, setIsFetched] = useState(false)
  const [idOldBankUser, setIdOldBankUser] = useState<any>({})
  const [isLoadingRole, setIsLoadingRole] = useState<boolean>(false)
  const [roleList, setRoleList] = useState<IGetListRole.Response>([])

  const [isModalConfirmActiveOpened, setIsModalConfirmActiveOpened] = useState(false)

  const getBankInfo = useCallback(async () => {
    if (id) {
      const res = await getBankUserDetail(id)

      const formattedData = mappingDataToFormCopy(res?.data)
      setIsFetched(true)

      return formattedData
    }
    navigate(menuUrls.bankUserSetup)
  }, [id, navigate])

  const {
    control,
    formState: { errors },
    handleSubmit,
    getValues,
    setError,
    setValue,
    trigger,
    clearErrors,
    resetField,
  } = useForm<any>({
    mode: 'all',
    defaultValues: async () => await getBankInfo(),
    resolver: yupResolver(generateValidationSchema(t)),
  })

  const handleCancel = () => {
    navigate(menuUrls.bankUserSetup)
  }

  const addBankUser = async (data: IBankUserSetup) => {
    setIsLoading(true)
    const convertedData = {
      ...data,
      role: data?.role,
    }
    const res = await addNewBankUser(formatDataBeforeSubmit(convertedData))
    const { statusCode } = res
    setIsLoading(false)

    if (statusCode === 'Success') {
      setIdOldBankUser(res?.data?.oldBankUser?.id)
      // Duplicate found
      if (!res?.data?.success) {
        switch (res?.data?.code) {
          case SERVER_STATUS_CODES.DUP01_CHECK_STATUS_DUPLICATED:
            setError('username', { message: t('SystemMessages:' + SYSTEM_MESSAGE_IDS.ALREADY_USERNAME) })
            break
          case SERVER_STATUS_CODES.DUP02_CHECK_STATUS_DUPLICATED:
            setIsModalConfirmActiveOpened(true)
            setUsernameConfirm(res?.data?.oldBankUser?.username)
            break
          case SERVER_STATUS_CODES.DUP03_CHECK_STATUS_DUPLICATED:
            setError('email', { message: t('SystemMessages:' + SYSTEM_MESSAGE_IDS.ALREADY_EMAIL) })
            break
          case SERVER_STATUS_CODES.DUP04_CHECK_STATUS_DUPLICATED:
            setIsModalConfirmActiveOpened(true)
            setUsernameConfirm(res?.data?.oldBankUser?.username)
            break
          case SERVER_STATUS_CODES.DUP05_CHECK_STATUS_DUPLICATED:
            setError('username', { message: t('SystemMessages:' + SYSTEM_MESSAGE_IDS.ALREADY_USERNAME) })
            break
          case SERVER_STATUS_CODES.DUP06_CHECK_STATUS_DUPLICATED:
            setError('email', { message: t('SystemMessages:' + SYSTEM_MESSAGE_IDS.ALREADY_EMAIL) })
            break
          default:
            break
        }
      }

      // No Duplicate
      if (res?.data?.success) {
        navigate(menuUrls.bankUserSetup, {
          state: {
            bankUserSetup: {
              type: 'success',
              message: t('SystemMessages:' + SYSTEM_MESSAGE_IDS.BANK_USER_CREATED_SUCCESSFULLY, {
                Username: data?.username,
              }),
            },
          },
        })
      }
    }
  }
  const onClose = () => {
    setIsModalConfirmActiveOpened(false)
  }
  const handleGetRoleList = useCallback(async () => {
    try {
      setIsLoadingRole(true)
      const listResponse = await getUserRoleListByType(priGrpTypCodeName.bankAccess)
      if (listResponse.statusCode === STATUS_CODES?.SUCCESS) {
        setRoleList(listResponse.data)
      }
    } catch (error) {
      console.log('ERROR: ', error)
    } finally {
      setIsLoadingRole(false)
    }
  }, [])

  useEffect(() => {
    handleGetRoleList()
  }, [handleGetRoleList])

  const isSaveButtonDisabled =
    !!Object.keys(errors)?.length ||
    !getValues('username')?.trim() ||
    !getValues('firstName')?.trim() ||
    !getValues('lastName')?.trim() ||
    !getValues('email')?.trim() ||
    !getValues('role')?.trim()

  return (
    <Form
      name='bank-user-setup-form'
      className='form-bank-user-container'
      layout='vertical'
      onFinish={handleSubmit(addBankUser)}
    >
      {isFetched ? (
        <>
          <BankInfo
            control={control}
            errors={errors}
            getValues={getValues}
            isDisable={false}
            setValue={setValue}
            trigger={trigger}
          />

          <SelectCpn
            control={control}
            errors={errors}
            title={t('BankSetup:addNewBankUser.label.selectApplicableRoles')}
            placeholder={t('BankSetup:addNewBankUser.placeholder.selectApplicableRoles')}
            options={roleList?.map((item) => ({
              label: item.name,
              value: item.id,
            }))}
            selectName='role'
            defaultSelectedValue={''}
            clearErrors={clearErrors}
            trigger={trigger}
            setValue={setValue}
            onSelectBlur={() => {
              trigger('role')
            }}
            loading={isLoadingRole}
          />

          <SelectClient
            control={control}
            errors={errors}
            clearErrors={clearErrors}
            resetField={resetField}
            setValue={setValue}
            includesArchived={false}
          />

          <Flex className='button-group' justify='flex-end' gap={24}>
            <Button className='cancel-button' onClick={handleCancel}>
              {t('OverallFunctionality:cancel_btn')}
            </Button>

            <Button
              className='save-button'
              type='primary'
              disabled={isSaveButtonDisabled}
              htmlType='button'
              onClick={handleSubmit(addBankUser)}
              loading={isLoading}
            >
              {t('OverallFunctionality:save_changes_btn')}
            </Button>
          </Flex>
          <ActionConfirmationModal
            icon={<DangerIcon />}
            title={t('BankSetup:addNewBankUser.modal.title.confirmation')}
            footer={[
              <Button size='middle' className='cancel-button' onClick={onClose}>
                {t('common:no')}
              </Button>,
              <Button type='primary' onClick={() => navigate(`${menuUrls.editBankUserSetup}/${idOldBankUser}`)}>
                {t('common:yes')}
              </Button>,
            ]}
            onClose={onClose}
            description={
              <RichTextField
                richText={
                  t('BankSetup:addNewBankUser.modal.description.descriptionDanger', {
                    Username: `${usernameConfirm}`,
                  }) || ''
                }
              />
            }
            descriptionType='string'
            isOpen={isModalConfirmActiveOpened}
          />
        </>
      ) : (
        <Skeleton />
      )}
    </Form>
  )
}

export default BankSetupCopy
